import { AI_SOLUTIONS_DIAGRAM_GIF, AI_AUTOMATION_DIAGRAM_GIF } from '../config/cloudinaryHomeImages';

/**
 * "Home page" content — a singleton (like aboutPage), editable the same way
 * as TemplateServicePage / EditableAboutPage: per-field text/image/icon/link
 * editing plus per-block style overrides and whole-section show/hide.
 *
 * Deliberately does NOT duplicate content already owned by another CMS
 * section: testimonial items (testimonials.items via the Testimonials tab),
 * case-study cards (caseStudies.cards via the Case Studies tab), and the 4
 * solution-tab video URLs (media.solutionVideos via the Media URLs tab) all
 * stay exactly where they are — only the heading/subheading text around them
 * lives here. DEFAULT_PAGE_CONTENT below IS the page's original hardcoded
 * copy (HeroSection.js + SolutionNeedSection.js + NumbersStatsSection.js +
 * AISolutionsSection.js + AIAutomationSection.js + HomeCaseStudiesSection.jsx
 * + WhyAISection.js + HowDatabiqsSection.js + TestimonialsSection.js +
 * MeetingBannerSection's DEFAULT_COPY), so the CMS-driven page renders
 * byte-identical to what shipped before.
 */
export const DEFAULT_PAGE_CONTENT = {
  hiddenSections: [],
  hero: {
    titleLine1: 'Enterprise AI',
    titleAccent: 'Solutions',
    titleLine2: 'Built to Scale',
    titleLine3: 'Your Business',
    description:
      'Build secure, high-performance AI automation, intelligent chatbots, and custom machine-learning solutions designed for reliable enterprise growth.',
    ctaLabel: 'Contact Us',
    ctaTo: '/contact',
    chips: [
      { text: 'Machine Learning' },
      { text: 'AI Strategy/Consultation' },
      { text: 'AI Automation/Implementation' },
      { text: 'AI Chatbot /Conversational' },
    ],
    quoteTag: 'Free · No Obligation',
    quoteTitle: 'Get a Free Project Quote',
    quoteDesc:
      "Tell us about your idea and we'll reply with a clear scope, timeline, and fixed price — usually within 24 hours.",
    quoteCtaLabel: 'Get a Quote',
    quoteCtaTo: '/contact',
    quoteTrust: ['Free consultation', 'Reply within 24h', 'No obligation'],
  },
  solutionNeed: {
    headingPrefix: 'What Kind of',
    headingAccent: 'Solution',
    headingSuffix: 'Do You Need?',
    subheading:
      "Assemble your ideal team with top-tier professionals who are passionate about turning your AI vision into production reality. Whether you're seeking Machine Learning engineers, AI strategists, or enterprise system architects, we have the specialized talent to match your project's technical requirements.",
    ctaLabel: 'View Details',
    options: [
      {
        id: 'automation',
        title: 'AI Automation / Implementation',
        shortLabel: 'AI Automation /\nImplementation',
        subheading:
          'End-to-end automation that connects your tools, data, and teams so repetitive work runs reliably in production - not just in a demo.',
        bullets: [
          'Cross-team workflow automation',
          'Reliable API integrations',
          'Audit-ready process controls',
          'Measurable operational outcomes',
        ],
        ctaTo: '/services/ai-automation',
        iconName: 'workflow',
      },
      {
        id: 'chatbots',
        title: 'AI Chatbots',
        shortLabel: 'AI Chatbots',
        subheading:
          'Conversational experiences that understand context, respect policies, and escalate cleanly when a human needs to take over.',
        bullets: [
          'Context-aware customer responses',
          'Smart intent routing',
          'Secure policy-compliant handling',
          'Continuous quality optimization',
        ],
        ctaTo: '/services/ai-chatbots',
        iconName: 'bot',
      },
      {
        id: 'ml',
        title: 'Machine Learning',
        shortLabel: 'Machine\nLearning',
        subheading:
          'Models and pipelines that turn historical and streaming data into forecasts, rankings, and decisions your teams can trust.',
        bullets: [
          'Forecasting and predictions',
          'Robust model pipelines',
          'Governance and compliance',
          'Production-ready deployment cycles',
        ],
        ctaTo: '/services/machine-learning',
        iconName: 'brain',
      },
      {
        id: 'strategy',
        title: 'AI Consulting',
        shortLabel: 'AI Consulting',
        subheading:
          'Strategic AI consulting designed to help organizations identify high-impact opportunities, align AI initiatives with business goals, and build scalable transformation roadmaps. ',
        bullets: [
          'AI Readiness & Opportunity Assessment',
          'AI Transformation Roadmaps',
          'Use-Case Prioritization & ROI Planning',
          'Process & Workflow Analysis',
        ],
        ctaTo: '/services/ai-consulting',
        iconName: 'lightbulb',
      },
    ],
  },
  numbersStats: {
    items: [
      { value: 'Up to 3x', label: 'FASTER TURNAROUND' },
      { value: '40%+', label: 'EFFICIENCY GAIN' },
      { value: '90%', label: 'FEWER MANUAL ERRORS' },
      { value: '80%', label: 'TASKS AUTOMATED' },
    ],
  },
  aiSolutions: {
    headingPrefix: 'Intelligent',
    headingAccent: 'AI Solutions',
    headingSuffix: 'Built for Scalable Growth',
    description:
      'We design AI-powered systems that streamline operations, enhance customer engagement, and enable businesses to scale with confidence.',
    cardTitle: 'AI Conversational & Support Systems',
    cardDescription:
      'AI-Powered Conversational And Support Systems Designed To Automate Interactions, Reduce Response Times, Assist Teams, And Deliver Consistent, High-Quality Customer Experiences Across All Digital Touchpoints.',
    imgSrc: AI_SOLUTIONS_DIAGRAM_GIF,
    imgAlt: 'AI Solutions Diagram',
    capabilitiesLabel: 'Key Capabilities:',
    capabilities: [
      'Conversational AI With Natural Language Understanding',
      '24/7 Automated Customer Interactions',
      'Intelligent Query Routing & Escalation',
      'Human-AI Collaboration For Support Teams',
      'Multi-Channel Integration (Web, Chat, Messaging Platforms)',
    ],
  },
  aiAutomation: {
    title: 'AI Automation',
    description:
      'Automate Workflows Using intelligent orchestration layers to Connect Systems, Eliminate Manual Tasks, And Optimize Business Operations.',
    imgSrc: AI_AUTOMATION_DIAGRAM_GIF,
    imgAlt: 'AI automation workflow animation',
    capabilitiesLabel: 'Key Capabilities:',
    capabilities: [
      'Custom Workflow Automation',
      'Seamless System Integration',
      'Data Synchronization',
      'Process Optimization',
    ],
  },
  homeCaseStudies: {
    headlinePrefix: 'Proven',
    headlineAccent: 'Impact',
    headlineSuffix: 'through AI Solutions',
    description:
      'Discover How We Help Forward-Thinking Businesses Solve Complex Challenges, Optimize Operations, And Achieve Measurable Results With Our AI-Driven Solutions.',
  },
  whyAI: {
    headingLine1Prefix: 'Why AI systems',
    headingLine1Accent: 'looks',
    headingLine1Suffix: 'powerful…',
    headingLine2Prefix: "but don't",
    headingLine2Accent: 'deliver',
    headingLine2Suffix: 'results',
    description: "Your System Works. The Technology Is Solid. But The Outcome Doesn't Match The Expectation.",
    complaintsLabel: 'Common Complaints',
    complaints: [
      { quote: '"We automated processes, but work is still manual"', sub: 'Efficiency gains never fully materialize.' },
      { quote: '"Our AI works in demos, but not in production"', sub: 'Real-world usage breaks flows and creates friction.' },
      { quote: "\"We don't know how to turn AI into real business value\"", sub: 'Use cases exist but have no path to impact.' },
      { quote: '"We have data, but no intelligence comes from it"', sub: 'Data lakes become expensive data graveyards.' },
      { quote: '"Our product is technically strong, but fails to scale"', sub: 'Architecture decisions limit growth potential.' },
    ],
    rightHeadingLine1: "We Don't Just Build Technology",
    rightHeadingLine2: 'We Engineer Business Outcomes',
    rightDescription:
      'Every Engagement Starts With Understanding Your Desired Outcome, Then Reverse-Engineers The Technology To Match. We Stay Until Results Are Proven.',
    differentiators: [
      'Architecture-First Thinking - No Shortcuts',
      'Production-Grade AI, Not Demoware',
      'Measurable ROI Aligned To Your KPIs',
      'Embedded Tech Leads, Not Black-Box Vendors',
      'Scale From Startup To Enterprise',
    ],
  },
  howDatabiqs: {
    headingPrefix: 'How',
    headingAccent: 'Databiqs',
    headingSuffix: 'can help?',
    description:
      'We move AI out of the lab and into production. Our strategic approach ensures that complex technological systems are architected for scale, deeply integrated into your workflows, and designed to deliver measurable business impact.',
    cards: [
      { iconName: 'search', title: '1. Audit', description: 'Identifying bottlenecks in existing data pipelines and legacy systems to determine high-ROI AI integration points.' },
      { iconName: 'compass', title: '2. Architecture', description: 'Designing resilient, scaleable infrastructures that support real-time processing and ensure enterprise-grade security.' },
      { iconName: 'refresh', title: '3. Automation', description: 'Operationalizing AI models within your business processes to eliminate manual work and drive continuous optimization.' },
    ],
  },
  testimonialsHeading: {
    headingPrefix: 'Trusted by',
    headingAccent: 'Forward-Thinking',
    headingSuffix: 'Businesses',
    description:
      'Our Clients Partner With Us To Solve Complex Challenges, Streamline Operations, And Achieve Measurable Results Through Intelligent AI Solutions.',
  },
  finalCta: {
    title: 'START YOUR TRANSFORMATION TODAY',
    subtitle: 'Setup A 30 Minutes Consultation Call With Our Experts. No Sales Pitch, Just Pure Engineering Strategy.',
    ctaLabel: 'Setup a consultation call',
    ctaTo: '/book-consultation',
  },
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function createHomePageTemplate() {
  return deepClone(DEFAULT_PAGE_CONTENT);
}

const mergeArrayOfObjects = (storedArr, tplArr) => {
  if (!Array.isArray(storedArr) || !storedArr.length) return tplArr;
  return storedArr.map((item, i) => ({ ...(tplArr[i] || tplArr[0] || {}), ...(item || {}) }));
};

/** Deep-merges stored (possibly partial/older) page data over the current defaults so the renderer never crashes. */
export function normalizeHomePageData(stored) {
  const tpl = createHomePageTemplate();
  if (!stored || typeof stored !== 'object') return tpl;

  return {
    hiddenSections: Array.isArray(stored.hiddenSections) ? stored.hiddenSections : tpl.hiddenSections,
    hero: {
      ...tpl.hero,
      ...(stored.hero || {}),
      chips: mergeArrayOfObjects(stored.hero?.chips, tpl.hero.chips),
      quoteTrust: Array.isArray(stored.hero?.quoteTrust) ? stored.hero.quoteTrust : tpl.hero.quoteTrust,
    },
    solutionNeed: {
      ...tpl.solutionNeed,
      ...(stored.solutionNeed || {}),
      options: mergeArrayOfObjects(stored.solutionNeed?.options, tpl.solutionNeed.options).map((opt, i) => ({
        ...opt,
        bullets: Array.isArray(opt.bullets) ? opt.bullets : tpl.solutionNeed.options[i]?.bullets || [],
      })),
    },
    numbersStats: {
      ...tpl.numbersStats,
      items: mergeArrayOfObjects(stored.numbersStats?.items, tpl.numbersStats.items),
    },
    aiSolutions: {
      ...tpl.aiSolutions,
      ...(stored.aiSolutions || {}),
      capabilities: Array.isArray(stored.aiSolutions?.capabilities)
        ? stored.aiSolutions.capabilities
        : tpl.aiSolutions.capabilities,
    },
    aiAutomation: {
      ...tpl.aiAutomation,
      ...(stored.aiAutomation || {}),
      capabilities: Array.isArray(stored.aiAutomation?.capabilities)
        ? stored.aiAutomation.capabilities
        : tpl.aiAutomation.capabilities,
    },
    homeCaseStudies: { ...tpl.homeCaseStudies, ...(stored.homeCaseStudies || {}) },
    whyAI: {
      ...tpl.whyAI,
      ...(stored.whyAI || {}),
      complaints: mergeArrayOfObjects(stored.whyAI?.complaints, tpl.whyAI.complaints),
      differentiators: Array.isArray(stored.whyAI?.differentiators)
        ? stored.whyAI.differentiators
        : tpl.whyAI.differentiators,
    },
    howDatabiqs: {
      ...tpl.howDatabiqs,
      ...(stored.howDatabiqs || {}),
      cards: mergeArrayOfObjects(stored.howDatabiqs?.cards, tpl.howDatabiqs.cards),
    },
    testimonialsHeading: { ...tpl.testimonialsHeading, ...(stored.testimonialsHeading || {}) },
    finalCta: { ...tpl.finalCta, ...(stored.finalCta || {}) },
  };
}

export function createDefaultHomePageStyles() {
  return {};
}

export function normalizeHomePageStyles(stored) {
  if (!stored || typeof stored !== 'object') return {};
  const out = {};
  for (const [blockId, val] of Object.entries(stored)) {
    if (!val || typeof val !== 'object') continue;
    out[blockId] = {
      desktop: val.desktop && typeof val.desktop === 'object' ? { ...val.desktop } : {},
      mobile: val.mobile && typeof val.mobile === 'object' ? { ...val.mobile } : {},
    };
  }
  return out;
}

/** Full normalized { data, styles } shape, the way it's stored under content.homePage. */
export function normalizeHomePage(stored) {
  return {
    data: normalizeHomePageData(stored?.data),
    styles: normalizeHomePageStyles(stored?.styles),
    updatedAt: stored?.updatedAt || new Date().toISOString(),
  };
}
